import { Injectable } from '@angular/core';

import { IIdentity } from '@grid/models';
import { Sync } from '../models/grid';

@Injectable()
export class MergeService {
  /**
   * @description Merges source into destination
   * @returns new object containing the updated destination
   */
  applyChanges<T extends IIdentity>(destination: T[], source: Sync<T>): T[] {
    if (!source) {
      return destination;
    }

    destination = this.applyAddChanges(destination, source.addedList);
    destination = this.applyUpdateChanges(destination, source.updatedList);
    destination = this.applyDeleteChanges(destination, source.deletedIds);

    return destination;
  }

  mergeChanges<T extends IIdentity>(...sources: Sync<T>[]): Sync<T> {
    return sources.copy().reduce((acc, curr) => {
      acc.addedList = this.applyUpdateChanges(acc.addedList, curr.addedList);
      acc.updatedList = this.applyUpdateChanges(
        acc.updatedList,
        curr.updatedList
      );
      acc.deletedIds = this.mergeDeleteChanges(acc.deletedIds, curr.deletedIds);

      return acc;
    });
  }

  applyAddChanges<T extends IIdentity>(destination: T[], addedList: T[]): T[] {
    if (!addedList?.length) {
      return destination;
    }

    if (!destination?.length) {
      destination = addedList;

      return destination;
    }

    // remove previously added items
    destination = destination.filter((x) => Number(x.id));

    destination = destination.concat(addedList || []);

    return destination;
  }

  applyUpdateChanges<T extends IIdentity>(
    destination: T[],
    updatedList: T[]
  ): T[] {
    if (!updatedList?.length) {
      return destination;
    }

    if (!destination?.length) {
      destination = updatedList;

      return destination;
    }

    updatedList.forEach(this.mergeItem.bind(this, destination));

    return destination;
  }

  applyDeleteChanges<T extends IIdentity>(
    destination: T[],
    deletedIds: (number | string)[]
  ): T[] {
    if (!deletedIds?.length) {
      return destination;
    }

    if (!destination?.length) {
      return destination;
    }

    destination = destination.filter(
      (item) => deletedIds.indexOf(item.id) === -1
    );

    return destination;
  }

  addDataToChanges<T extends IIdentity>(
    changes: Sync<T>,
    data: T,
    added: boolean
  ): Sync<T> {
    const result: Sync<T> = changes ?? {
      addedList: [],
      updatedList: [],
      deletedIds: [],
    };

    if (added) {
      const addIndex = result.addedList.findIndex((x) => x.id === data.id);

      if (addIndex !== -1) {
        result.addedList[addIndex] = data;
      } else {
        result.addedList.push(data);
      }
    } else {
      const updateIndex = result.updatedList.findIndex((x) => x.id === data.id);

      if (updateIndex !== -1) {
        result.updatedList[updateIndex] = data;
      } else {
        result.updatedList.push(data);
      }
    }

    return result;
  }

  //#region Private methods

  /**
   * @description Adds or replace object item into destination array based on item id
   * @returns destination array
   */
  private mergeItem<T extends IIdentity>(destination: T[], item: T): T[] {
    if (!destination?.length) {
      destination = [item];

      return destination;
    }

    const index = destination.findIndex((x) => x.id === item.id);

    if (index !== -1) {
      destination[index] = item;
    } else {
      destination.push(item);
    }

    return destination;
  }

  private mergeDeleteChanges(
    destination: (number | string)[],
    deletedIds: (number | string)[]
  ): (number | string)[] {
    if (!deletedIds?.length) {
      return destination;
    }

    if (!destination?.length) {
      destination = deletedIds;

      return destination;
    }

    return [...new Set([...destination, ...deletedIds])];
  }

  //#endregion
}
